/**
 * WorkspaceSettingsPage Component
 *
 * Main page for workspace settings with tabs for Members, Groups, and Settings
 */

import React, { useState } from 'react';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import { useAuth } from '../../../contexts/AuthContext';
import { Settings, Users, UsersRound, Building2, Bot, Key } from 'lucide-react';
import { WorkspaceMembersTab } from '../ui/WorkspaceMembersTab';
import { WorkspaceGroupsTab } from '../ui/WorkspaceGroupsTab';
import { WorkspaceSettingsTab } from '../ui/WorkspaceSettingsTab';
import { WorkspaceAgentsTab } from '../ui/WorkspaceAgentsTab';
import { WorkspaceApiKeysTab } from '../ui/WorkspaceApiKeysTab';
import { Workspace } from '../../../types/workspace';

type TabType = 'settings' | 'members' | 'groups' | 'agents' | 'api';

interface WorkspaceSettingsPageProps {
  preSelectedWorkspace?: Workspace;
}

export const WorkspaceSettingsPage: React.FC<WorkspaceSettingsPageProps> = ({ preSelectedWorkspace }) => {
  const { currentWorkspace, isLoading } = useWorkspace();
  const { user: currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('settings');

  // Use preSelectedWorkspace if provided (admin mode), otherwise use currentWorkspace
  const workspace = preSelectedWorkspace || currentWorkspace;

  if (isLoading && !preSelectedWorkspace) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#1a1a1a]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F4CC04]"></div>
      </div>
    );
  }

  if (!workspace) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-[#1a1a1a]">
        <Building2 className="w-16 h-16 text-[#888888] mb-4" />
        <h2 className="text-xl font-semibold text-white mb-2">Nenhum Workspace Selecionado</h2>
        <p className="text-[#888888]">Selecione um workspace para gerenciar configurações</p>
      </div>
    );
  }

  const canManageApi =
    !!currentUser &&
    (currentUser.isSuperAdmin ||
      workspace.owner_id === currentUser.id ||
      currentUser.canAdmin ||
      currentUser.canManageWorkspace);

  const tabs = [
    { id: 'settings' as TabType, label: 'Configurações', icon: Settings },
    { id: 'members' as TabType, label: 'Membros', icon: Users },
    { id: 'groups' as TabType, label: 'Grupos', icon: UsersRound },
    { id: 'agents' as TabType, label: 'Agentes', icon: Bot },
    ...(canManageApi ? [{ id: 'api' as TabType, label: 'API', icon: Key }] : []),
  ];

  return (
    <div className="min-h-screen bg-[#1a1a1a]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Configurações do Workspace</h1>
          <p className="text-[#888888]">
            Gerenciar workspace {workspace.name}
            {workspace.is_td_workspace && (
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-500/20 text-purple-400">
                TD Workspace
              </span>
            )}
          </p>
        </div>

        {/* Tabs */}
        <div className="bg-[#2a2a2a] rounded-xl border border-[#333333]">
          {/* Tab Headers */}
          <div className="border-b border-[#333333]">
            <nav className="flex -mb-px">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;

                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                      isActive
                        ? 'border-[#F4CC04] text-[#F4CC04]'
                        : 'border-transparent text-[#888888] hover:text-white hover:border-[#444444]'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'settings' && <WorkspaceSettingsTab workspace={workspace} />}
            {activeTab === 'members' && <WorkspaceMembersTab workspace={workspace} />}
            {activeTab === 'groups' && <WorkspaceGroupsTab workspace={workspace} />}
            {activeTab === 'agents' && <WorkspaceAgentsTab workspace={workspace} />}
            {activeTab === 'api' && <WorkspaceApiKeysTab workspace={workspace} />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkspaceSettingsPage;
